/**
 * What a commercial module declares about itself.
 *
 * COMMERCIAL_MODULARITY §3: the registry is "declarative and version-controlled,
 * reviewed like schema. A module cannot be sold until it is registered." So a
 * manifest is a source file under CODEOWNERS review, not a database row an
 * administrator can add — and every enforcement point resolves through it,
 * because §3 also says nothing hardcodes a module name in a conditional.
 *
 * A manifest lives with its module. Stage 2 has no `packages/modules/**`, so
 * the manifests sit here in the registry; each is a `module.manifest.ts` in its
 * own directory, which is the path they keep when they move.
 */
/**
 * `core` is mandatory and unsellable; `commercial` is a functional SKU;
 * `add_on` is an independently licensable product surface over shared Core
 * infrastructure (C-1, C-2).
 */
export const MODULE_KINDS = ['core', 'commercial', 'add_on'];
export const MODULE_LIFECYCLES = ['proposed', 'available', 'deprecated', 'withdrawn'];
/**
 * Which commercial release a module belongs to.
 *
 * NOT `sellable`, and not `lifecycle`, because it is a third question.
 * `sellable` asks whether the module can ever be a SKU — Platform Core is
 * permanently false. `lifecycle` asks whether it is built. This asks whether
 * the approved commercial model offers it *now*.
 *
 * The three genuinely come apart. Field Service is `sellable: true` because
 * `finance.optionalModules` names it, and the registry refuses an optional
 * dependency on an unsellable module (§5) — but it is not in the approved
 * Phase 1 model, so it must not appear in navigation and must not be granted.
 * Overloading `sellable` to mean "offered now" would break that validation and
 * silently rewrite what §5 guarantees.
 */
export const MODULE_RELEASES = ['phase_1', 'later'];
/**
 * The limit types the platform can express (§4.2). A module declares which
 * apply to it; what the threshold is, and what happens at it, is per-tenant
 * commercial configuration and appears nowhere in a manifest.
 */
export const LIMIT_TYPES = ['user', 'active_user', 'legal_entity', 'site', 'transaction'];
/** warn: notify and allow · block: refuse · grace: allow for a window, then block. */
export const LIMIT_POLICIES = ['warn', 'block', 'grace'];
/**
 * How a metered event is counted.
 *
 * `active_user` is its own unit rather than a count, because C-3 makes the
 * active-user metric a count of DISTINCT people in a period and that is not
 * derivable from a total: "1,000 actions" and "3 people" are different numbers
 * and only one of them is what a seat is sold against.
 */
export const METER_UNITS = ['count', 'active_user', 'byte', 'second'];
/** Identity helper, so a manifest file reads as data and is still typed. */
export function defineModule(manifest) {
    return manifest;
}
//# sourceMappingURL=manifest.js.map